import type { DropDownResult } from '../fields/drop-down/drop-down-types';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import i18next from 'i18next';

const USER_TIME_ZONE = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;
const FORMATTER = Intl.DateTimeFormat(navigator.language, { dateStyle: 'short', timeZone: USER_TIME_ZONE });

export const formatDateRange = (from?: Date, to?: Date) => {
  const fromValue = from ? FORMATTER.format(from) : '';
  const toValue = to ? FORMATTER.format(to) : '';
  return `${fromValue} - ${toValue}`;
};

// Default list of date range options
export const getDateRanges = (): Array<DropDownResult> => [
  { value: 'LAST_24_HOURS', text: i18next.t('date-range-last-24-hours') },
  { value: 'LAST_7_DAYS', text: i18next.t('date-range-last-7-days') },
  { value: 'LAST_30_DAYS', text: i18next.t('date-range-last-30-days') },
  { value: 'LAST_12_MONTHS', text: i18next.t('date-range-last-12-months') },
  { value: 'CURRENT_MONTH', text: i18next.t('date-range-current-month') },
  { value: 'CURRENT_YEAR', text: i18next.t('date-range-current-year') },
  { value: 'PREVIOUS_DAY', text: i18next.t('date-range-previous-day') },
  { value: 'PREVIOUS_WEEK', text: i18next.t('date-range-previous-week') },
  { value: 'PREVIOUS_MONTH', text: i18next.t('date-range-previous-month') },
  { value: 'PREVIOUS_YEAR', text: i18next.t('date-range-previous-years') },
];

export const getCustomDateRangeItem = (): DropDownResult => ({
  value: 'CUSTOM_RANGE',
  text: i18next.t('date-range-custom'),
});

// Calculates the date range based on the quick date range selection
export const calculateDateRange = (range: DropDownResult) => {
  const today = new Date();

  if ('LAST_24_HOURS' === range.value) {
    return {
      from: subDays(today, 1),
      to: today,
    };
  } else if ('LAST_7_DAYS' === range.value) {
    return {
      from: startOfDay(subDays(today, 7)),
      to: endOfDay(subDays(today, 1)),
    };
  } else if ('LAST_30_DAYS' === range.value) {
    return {
      from: startOfDay(subDays(today, 30)),
      to: endOfDay(subDays(today, 1)),
    };
  } else if ('LAST_12_MONTHS' === range.value) {
    return {
      from: startOfDay(subYears(today, 1)),
      to: endOfDay(today),
    };
  } else if ('CURRENT_MONTH' === range.value) {
    return {
      from: startOfMonth(today),
      to: endOfDay(today),
    };
  } else if ('CURRENT_YEAR' === range.value) {
    return {
      from: startOfYear(today),
      to: endOfDay(today),
    };
  } else if ('PREVIOUS_DAY' === range.value) {
    return {
      from: startOfDay(subDays(today, 1)),
      to: endOfDay(subDays(today, 1)),
    };
  } else if ('PREVIOUS_WEEK' === range.value) {
    const previousWeekStart = subWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1);
    return {
      from: previousWeekStart,
      to: endOfWeek(previousWeekStart, { weekStartsOn: 1 }),
    };
  } else if ('PREVIOUS_MONTH' === range.value) {
    return {
      from: startOfMonth(subMonths(today, 1)),
      to: endOfMonth(subMonths(today, 1)),
    };
  } else if ('PREVIOUS_YEAR' === range.value) {
    return {
      from: startOfYear(subYears(today, 1)),
      to: endOfYear(subYears(today, 1)),
    };
  }

  return undefined;
};
